import cx from "classnames";
import React, { useMemo, useState } from "react";
import { Button, Col, Form, FormFeedback, FormGroup, Input } from "reactstrap";

import { escape } from "@common/util";
import { ATTR_ID } from "../../../constants";
import { TableBlock } from "../../../types";
import {
  ATTR_COL,
  ATTR_COLSPAN,
  ATTR_MAX_X,
  ATTR_MAX_Y,
  ATTR_MIN_X,
  ATTR_MIN_Y,
  ATTR_ROW,
  ATTR_ROWSPAN,
  TAG_COL,
  TAG_ROW,
} from "../constants";
import { Props as ContentProps } from "../types";
import { extractFragmentFromBlock, insertFragmentIntoBlock } from "../util";

type Props = Readonly<{
  block: ContentProps<TableBlock>["block"];
  onCancel: () => void;
  onSave: (correction: string) => void;
  onSelectBlock: ContentProps<TableBlock>["onSelectBlock"];
  pages: ContentProps<TableBlock>["pages"];
  previewImage: React.ReactElement;
}>;

const extractTableFragmentFromBlock = (block: Element): string => {
  const ignoreRowAttrs = [ATTR_ROW, "xmlns:inno"];
  const ignoreColAttrs = [
    ATTR_COL,
    ATTR_ID,
    ATTR_MAX_X,
    ATTR_MIN_X,
    ATTR_MAX_Y,
    ATTR_MIN_Y,
    "xmlns:inno",
  ];
  const toReturn: Array<string> = [];
  const rows = Array.from(block.getElementsByTagName(TAG_ROW));
  for (const row of rows) {
    const rowAttrs = Array.from(row.attributes)
      .filter(attr => ignoreRowAttrs.indexOf(attr.name) < 0)
      .map(attr => `${attr.name}="${escape(attr.value)}"`);
    let rowAttrString = "";
    if (rowAttrs.length > 0) {
      rowAttrString = " " + rowAttrs.join(" ");
    }
    const cols = Array.from(row.getElementsByTagName(TAG_COL));
    if (cols.length < 1) {
      toReturn.push(`<tr${rowAttrString}/>`);
      continue;
    }
    const columns: Array<string> = cols.map((col: Element): string => {
      const colAttrs = Array.from(col.attributes)
        .filter(attr => ignoreColAttrs.indexOf(attr.name) < 0)
        .filter(attr => {
          if (attr.name === ATTR_COLSPAN || attr.name === ATTR_ROWSPAN) {
            const spanValue = parseInt(attr.value, 10);
            if (isNaN(spanValue) || spanValue === 1) {
              return false;
            }
          }
          return true;
        })
        .map(attr => `${attr.name}="${escape(attr.value)}"`);
      let colAttrString = "";
      if (colAttrs.length > 0) {
        colAttrString = " " + colAttrs.join(" ");
      }
      return `<td${colAttrString}>${extractFragmentFromBlock(col)}</td>`;
    });
    toReturn.push(`<tr${rowAttrString}>${columns.join("")}</tr>`);
  }
  return toReturn.join("");
};

const injectTableAttributes = (block: Element, tableId: string): Element => {
  const rows = block.getElementsByTagName(TAG_ROW);
  for (let i = 0; i < rows.length; i++) {
    rows[i].setAttribute(ATTR_ROW, `${i + 1}`);
    const cols = rows[i].getElementsByTagName(TAG_COL);
    for (let j = 0; j < cols.length; j++) {
      cols[j].setAttribute(ATTR_COL, `${j + 1}`);
      cols[j].setAttribute(ATTR_ID, `${tableId}-${i + 1}-${j + 1}`);
      let colSpan = parseInt(cols[j].getAttribute(ATTR_COLSPAN) || "", 10);
      if (isNaN(colSpan) || colSpan < 1) {
        colSpan = 1;
      }
      let rowSpan = parseInt(cols[j].getAttribute(ATTR_ROWSPAN) || "", 10);
      if (isNaN(rowSpan) || rowSpan < 1) {
        rowSpan = 1;
      }
      cols[j].setAttribute(ATTR_COLSPAN, `${colSpan}`);
      cols[j].setAttribute(ATTR_ROWSPAN, `${rowSpan}`);
    }
  }
  return block;
};

export const Editing = ({ block, onCancel, onSave, previewImage }: Props) => {
  const initialState = useMemo(() => extractTableFragmentFromBlock(block.element), [block.element]);
  const [contentText, setContentText] = useState<string>(initialState);
  const [xmlValid, setXmlValid] = useState(true);
  return (
    <Form
      className="border bg-light p-3 mb-2"
      onSubmit={e => {
        e.preventDefault();
        const newBlock = injectTableAttributes(
          insertFragmentIntoBlock(block.element.cloneNode() as Element, contentText),
          block.id,
        );
        onSave(newBlock.innerHTML);
      }}
    >
      <FormGroup className="mb-0" row>
        <Col>
          <div className="m-auto">{previewImage}</div>
          <Input
            autoFocus
            className="d-block mb-2"
            invalid={!xmlValid}
            onChange={e => {
              setContentText(e.target.value);
              try {
                const clone = block.element.cloneNode() as Element;
                insertFragmentIntoBlock(clone, e.target.value);
                setXmlValid(true);
              } catch (err) {
                setXmlValid(false);
              }
            }}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === "Escape") {
                onCancel();
              }
            }}
            rows={6}
            type="textarea"
            valid={xmlValid}
            value={contentText}
          />
        </Col>
      </FormGroup>
      <div className="d-flex justify-content-between align-items-center">
        <Button color="secondary" onClick={onCancel} type="button">
          Cancel
        </Button>
        <FormFeedback
          className="d-block flex-fill text-center"
          invalid={!xmlValid}
          valid={xmlValid}
        >
          <i className={cx("fa", "mr-2", { "fa-check": xmlValid, "fa-times": !xmlValid })} />
          {xmlValid ? "XML is valid" : "XML is invalid"}
        </FormFeedback>
        <Button color="primary" disabled={!xmlValid} type="submit">
          Save
        </Button>
      </div>
    </Form>
  );
};
Editing.displayName = "Editing";
